import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis
} from 'recharts'
import { useTranslation } from 'react-i18next'

import type {
  AnalyticsBreakdownDimension,
  EventAnalyticsBreakdown
} from '@services/event-analytics/types'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig
} from '@/components/ui/chart'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

const DIMENSIONS: AnalyticsBreakdownDimension[] = [
  'listing_type',
  'source',
  'is_authenticated',
  'locale',
  'event',
  'location',
  'index_category'
]

const chartConfig = {
  count: { label: 'Count', color: '#11adbb' }
} satisfies ChartConfig

interface EventAnalyticsBreakdownChartProps {
  data?: EventAnalyticsBreakdown
  loading?: boolean
  dimension: AnalyticsBreakdownDimension
  onDimensionChange: (dimension: AnalyticsBreakdownDimension) => void
}

export function EventAnalyticsBreakdownChart({
  data,
  loading = false,
  dimension,
  onDimensionChange
}: EventAnalyticsBreakdownChartProps) {
  const { t } = useTranslation('eventAnalytics')

  const chartData =
    data?.items.map((item) => ({
      label: t(`breakdownValues.${item.value}`, {
        defaultValue: item.value
      }),
      count: item.count
    })) ?? []

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle>{t('charts.breakdownTitle')}</CardTitle>
          <CardDescription>{t('charts.breakdownDescription')}</CardDescription>
        </div>
        <Select
          value={dimension}
          onValueChange={(v) =>
            onDimensionChange(v as AnalyticsBreakdownDimension)
          }
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DIMENSIONS.map((d) => (
              <SelectItem key={d} value={d}>
                {t(`dimensions.${d}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-[280px] animate-pulse rounded-xl bg-ink/[0.04]" />
        ) : chartData.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            {t('eventsTable.empty')}
          </p>
        ) : (
          <ChartContainer config={chartConfig} className="h-[280px] w-full">
            <BarChart data={chartData} layout="vertical" margin={{ left: 8 }}>
              <CartesianGrid horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} />
              <YAxis
                type="category"
                dataKey="label"
                width={120}
                tickLine={false}
                axisLine={false}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar
                dataKey="count"
                fill="var(--color-count)"
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  )
}
